import { useEffect } from "react";

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  schema?: Record<string, any>;
  noIndex?: boolean;
}

const DEFAULT_TITLE = "FeelTalk — Someone to Talk To, Anytime";
const DEFAULT_DESCRIPTION =
  "FeelTalk connects you with compassionate, verified listeners 24/7 for safe, anonymous, and confidential emotional support.";
const DEFAULT_KEYWORDS =
  "emotional support, talk to someone, mental wellness, anonymous chat, online listener, empathy chat, mental health support";
const DEFAULT_IMAGE = "https://feeltalk.live/og-image.png";
const DEFAULT_URL = "https://feeltalk.live";

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  url = DEFAULT_URL,
  type = "website",
  schema,
  noIndex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | FeelTalk` : DEFAULT_TITLE;

  useEffect(() => {
    // Document Title
    document.title = fullTitle;

    // Helper to set meta tag by name or property
    const setMetaTag = (selector: string, attrName: string, attrVal: string, content: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrVal);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Helper to set link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // Standard Meta Tags
    setMetaTag('meta[name="description"]', "name", "description", description);
    setMetaTag('meta[name="keywords"]', "name", "keywords", keywords);
    setMetaTag(
      'meta[name="robots"]',
      "name",
      "robots",
      noIndex ? "noindex, nofollow" : "index, follow"
    );

    // Open Graph
    setMetaTag('meta[property="og:title"]', "property", "og:title", fullTitle);
    setMetaTag('meta[property="og:description"]', "property", "og:description", description);
    setMetaTag('meta[property="og:image"]', "property", "og:image", image);
    setMetaTag('meta[property="og:url"]', "property", "og:url", url);
    setMetaTag('meta[property="og:type"]', "property", "og:type", type);

    // Twitter Card
    setMetaTag('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    setMetaTag('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMetaTag('meta[name="twitter:image"]', "name", "twitter:image", image);
    setMetaTag('meta[name="twitter:url"]', "name", "twitter:url", url);
    setMetaTag('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");

    // Canonical Link
    setLinkTag("canonical", url);

    // JSON-LD Schema
    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (schema) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.setAttribute("type", "application/ld+json");
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    } else if (scriptTag) {
      // Default WebSite Schema if none passed
      const defaultSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "FeelTalk",
        url: "https://feeltalk.live",
        description: DEFAULT_DESCRIPTION,
        publisher: {
          "@type": "Organization",
          name: "FeelTalk Inc.",
          logo: "https://feeltalk.live/logo.svg",
        },
      };
      scriptTag.textContent = JSON.stringify(defaultSchema);
    }
  }, [fullTitle, description, keywords, image, url, type, schema, noIndex]);

  return null;
}
